/** @jsxImportSource @emotion/react */

'use client';

import { createContext, type ReactNode, useCallback, useContext, useReducer } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { NotificationContainer } from './NotificationContainer';
import type { NotificationMessage, NotificationStack, NotificationType } from './types';

// State management
interface NotificationState {
  stacks: Partial<Record<NotificationType, NotificationStack>>;
}

type Action =
  | {
      type: 'ADD_MESSAGE';
      payload: { notificationType: NotificationType; message: NotificationMessage };
    }
  | {
      type: 'NAVIGATE';
      payload: { notificationType: NotificationType; direction: 'prev' | 'next' };
    }
  | { type: 'CLOSE_STACK'; payload: { notificationType: NotificationType } }
  | { type: 'REMOVE_MESSAGE'; payload: { notificationType: NotificationType; messageId: string } };

function notificationReducer(state: NotificationState, action: Action): NotificationState {
  switch (action.type) {
    case 'ADD_MESSAGE': {
      const { notificationType, message } = action.payload;
      const existingStack = state.stacks[notificationType];

      if (existingStack) {
        // Add to existing stack
        return {
          ...state,
          stacks: {
            ...state.stacks,
            [notificationType]: {
              ...existingStack,
              messages: [...existingStack.messages, message],
              currentIndex: existingStack.messages.length, // Point to new message
            },
          },
        };
      } else {
        // Create new stack
        return {
          ...state,
          stacks: {
            ...state.stacks,
            [notificationType]: {
              type: notificationType,
              messages: [message],
              currentIndex: 0,
            },
          },
        };
      }
    }

    case 'NAVIGATE': {
      const { notificationType, direction } = action.payload;
      const stack = state.stacks[notificationType];

      if (!stack || stack.messages.length <= 1) return state;

      let newIndex = stack.currentIndex;
      if (direction === 'next') {
        newIndex = (stack.currentIndex + 1) % stack.messages.length;
      } else {
        newIndex = stack.currentIndex === 0 ? stack.messages.length - 1 : stack.currentIndex - 1;
      }

      return {
        ...state,
        stacks: {
          ...state.stacks,
          [notificationType]: {
            ...stack,
            currentIndex: newIndex,
          },
        },
      };
    }

    case 'CLOSE_STACK': {
      const { notificationType } = action.payload;
      const { [notificationType]: removed, ...remainingStacks } = state.stacks;

      return {
        ...state,
        stacks: remainingStacks,
      };
    }

    case 'REMOVE_MESSAGE': {
      const { notificationType, messageId } = action.payload;
      const stack = state.stacks[notificationType];

      if (!stack) return state;

      const newMessages = stack.messages.filter((msg) => msg.id !== messageId);

      // If no messages left, remove the entire stack
      if (newMessages.length === 0) {
        const { [notificationType]: removed, ...remainingStacks } = state.stacks;
        return {
          ...state,
          stacks: remainingStacks,
        };
      }

      // Adjust current index if needed
      let newCurrentIndex = stack.currentIndex;
      const removedIndex = stack.messages.findIndex((msg) => msg.id === messageId);

      if (removedIndex <= stack.currentIndex && stack.currentIndex > 0) {
        newCurrentIndex = stack.currentIndex - 1;
      } else if (newCurrentIndex >= newMessages.length) {
        newCurrentIndex = newMessages.length - 1;
      }

      return {
        ...state,
        stacks: {
          ...state.stacks,
          [notificationType]: {
            ...stack,
            messages: newMessages,
            currentIndex: newCurrentIndex,
          },
        },
      };
    }

    default:
      return state;
  }
}

// Context
const NotificationContext = createContext<{
  addMessage: (type: NotificationType, content: string | ReactNode) => void;
} | null>(null);

export function useNotify() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotify must be used within NotificationProvider');
  }

  const { addMessage } = context;

  return {
    default: (message: ReactNode | string) => addMessage('default', message),
    success: (message: ReactNode | string) => addMessage('success', message),
    warning: (message: ReactNode | string) => addMessage('warning', message),
    error: (message: ReactNode | string) => addMessage('error', message),
  };
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(notificationReducer, { stacks: {} });

  const addMessage = useCallback((type: NotificationType, content: string | ReactNode) => {
    const message: NotificationMessage = {
      id: uuidv4(),
      content,
      timestamp: Date.now(),
    };

    dispatch({ type: 'ADD_MESSAGE', payload: { notificationType: type, message } });
  }, []);

  const handleNavigate = useCallback((type: NotificationType, direction: 'prev' | 'next') => {
    dispatch({ type: 'NAVIGATE', payload: { notificationType: type, direction } });
  }, []);

  const handleCloseStack = useCallback((type: NotificationType) => {
    dispatch({ type: 'CLOSE_STACK', payload: { notificationType: type } });
  }, []);

  const handleRemoveMessage = useCallback((type: NotificationType, messageId: string) => {
    dispatch({ type: 'REMOVE_MESSAGE', payload: { notificationType: type, messageId } });
  }, []);

  return (
    <NotificationContext.Provider value={{ addMessage }}>
      {children}
      <NotificationContainer
        stacks={state.stacks}
        onNavigate={handleNavigate}
        onRemoveMessage={handleRemoveMessage}
        onCloseStack={handleCloseStack}
      />
    </NotificationContext.Provider>
  );
}
